package link.erpango.quip.features.myspareparts

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.domain.entities.DMySparePart
import link.erpango.quip.domain.usecases.IMySparePartsUseCase

/**
 * Estado de UI de la pantalla "Mis repuestos".
 * `error` no nulo indica que la última carga falló (puede estar vacío: mostrar mensaje genérico).
 */
data class MySparePartsUiState(
    val parts: List<DMySparePart> = emptyList(),
    val isLoading: Boolean = true,
    val isRefreshing: Boolean = false,
    val error: String? = null,
    val searchQuery: String = "",
    val selectedVehicleVin: String? = null
)

class MySparePartsViewModel(
    private val mySparePartsUseCase: IMySparePartsUseCase,
    /**
     * VIN fijo de filtro para la carga inicial (sección "Repuestos" del detalle de vehículo).
     * `null` (por defecto) mantiene el comportamiento de la lista global.
     */
    initialVehicleVin: String? = null
) : ViewModel() {

    companion object {
        const val SEARCH_DEBOUNCE_MS = 300L
    }

    private val _uiState = MutableStateFlow(
        MySparePartsUiState(selectedVehicleVin = initialVehicleVin)
    )
    val uiState: StateFlow<MySparePartsUiState> = _uiState.asStateFlow()

    // Job de la última carga disparada por búsqueda/filtro, para poder cancelarla
    // y evitar que resultados obsoletos se escriban en el estado.
    private var searchJob: Job? = null

    init {
        loadParts()
    }

    /**
     * Búsqueda con debounce de [SEARCH_DEBOUNCE_MS] ms. Cada cambio cancela la carga
     * pendiente (también si está en vuelo), de modo que nunca se aplican resultados obsoletos.
     */
    fun onSearchQueryChange(query: String) {
        _uiState.update { it.copy(searchQuery = query, error = null) }
        searchJob?.cancel()
        searchJob = viewModelScope.launch {
            if (query.isNotBlank()) delay(SEARCH_DEBOUNCE_MS)
            loadPartsInternal()
        }
    }

    /**
     * Cambia el filtro por vehículo del garaje. `null` significa "todos los vehículos".
     */
    fun onVehicleFilterChange(vehicleVin: String?) {
        _uiState.update { it.copy(selectedVehicleVin = vehicleVin, error = null) }
        searchJob?.cancel()
        searchJob = viewModelScope.launch {
            loadPartsInternal()
        }
    }

    /** Pull-to-refresh. Ignorado si ya hay una actualización en curso. */
    fun refresh() {
        if (_uiState.value.isRefreshing || _uiState.value.isLoading) return
        _uiState.update { it.copy(isRefreshing = true, error = null) }
        viewModelScope.launch {
            try {
                loadPartsInternal()
            } finally {
                _uiState.update { it.copy(isRefreshing = false) }
            }
        }
    }

    /** Reintenta la última carga tras un error. */
    fun retry() {
        searchJob?.cancel()
        searchJob = viewModelScope.launch {
            loadPartsInternal()
        }
    }

    /** True si la carga en curso ha sido cancelada (nueva búsqueda/filtro). */
    private suspend fun isLoadCancelled(): Boolean = currentCoroutineContext()[Job]?.isCancelled == true

    private suspend fun loadPartsInternal() {
        val state = _uiState.value
        if (state.parts.isEmpty()) {
            _uiState.update { it.copy(isLoading = true, error = null) }
        } else {
            _uiState.update { it.copy(error = null) }
        }
        try {
            val result = mySparePartsUseCase.listMySpareParts(
                vehicleVin = state.selectedVehicleVin,
                brandId = null,
                modelId = null,
                search = state.searchQuery.ifBlank { null }
            )
            // Si la carga fue cancelada (nueva búsqueda/filtro), no aplicamos el resultado.
            if (isLoadCancelled()) return
            when (result) {
                is DResponse.Success -> _uiState.update { it.copy(parts = result.data) }
                else -> _uiState.update {
                    it.copy(error = (result as? DResponse.Error)?.error ?: "")
                }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            if (!isLoadCancelled()) {
                _uiState.update { it.copy(error = e.message ?: "") }
            }
        } finally {
            if (!isLoadCancelled()) {
                _uiState.update { it.copy(isLoading = false, isRefreshing = false) }
            }
        }
    }

    private fun loadParts() {
        searchJob?.cancel()
        searchJob = viewModelScope.launch {
            loadPartsInternal()
        }
    }
}
